import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUsersQuery } from 'api';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { UserDataType } from 'pages/admin/users';
import { TablePaginationConfig, TableProps } from 'components';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { ErrorMappingValueType, OrderType, PaginationType, SortParamsType } from 'typings';
import { useCountryMapping } from 'hooks/useCountryMapping';
import { User } from 'models/User';
import { useLanguageMapping } from 'hooks/useLanguageMapping';
import { AssignedUser } from 'pages/admin/roles/useRoles';
import { useNotificationApi } from 'hooks/useNotificationApi';

export interface IFilterParams {
  department?: string;
  title?: string;
  roleId?: string;
  countryId?: string;
  languages?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  lastModifiedDateFrom?: string;
  lastModifiedDateTo?: string;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useUsers = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [drawerState, setDrawerState] = useState<boolean>(false);
  const [assignedUsersData, setAssignedUsersData] = useState<AssignedUser[] | undefined>(undefined);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { countryMapping } = useCountryMapping();
  const { languageMapping } = useLanguageMapping();
  const location = useLocation();
  const openNotificationWithIcon = useNotificationApi();
  const notificationHandledRef = useRef(false);
  const navigate = useNavigate();
  const { data: { data: { content = [], totalElements = 0 } = {} } = {}, isLoading } = useUsersQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });

  const usersList = useMemo(() => {
    const massagedData = content.map((i: User) => {
      let user = structuredClone(i);
      if (user.countryId) {
        user = {
          ...user,
          country: {
            name: countryMapping[i.countryId]?.commonName,
            flagLocation: countryMapping[i.countryId]?.flagLocation,
          },
        };
      }
      user.languagesData = (user.languages || [])?.map((_item: string) => languageMapping[_item]);
      return user;
    });
    return massagedData || [];
  }, [content, countryMapping, languageMapping]);

  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const onExpand = useCallback((users?: AssignedUser[]) => {
    setAssignedUsersData(users);
    setDrawerState((prevState) => !prevState);
  }, []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.users.view}/${value}/account`);
    },
    [navigate]
  );
  const onRedirectToRole = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.role.view}/${value}/${ADMIN_ROUTES.role.permissions}`);
    },
    [navigate]
  );

  const onAddUser = useCallback(() => {
    navigate(ADMIN_ROUTES.users.create);
  }, [navigate]);

  const handleTableChange: TableProps<UserDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<UserDataType> | SorterResult<UserDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.userId && { userId: values.userId }),
        ...(values.title && { title: values.title }),
        ...(values.department && { department: values.department }),
        ...(values.roleIds?.length && { roleIds: values.roleIds }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.languages?.length && { languages: values.languages }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastModifiedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastModifiedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  useEffect(() => {
    if (location.state?.notification && !notificationHandledRef.current) {
      notificationHandledRef.current = true;
      const { type, message }: ErrorMappingValueType = location.state.notification;
      openNotificationWithIcon({ type, message });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, openNotificationWithIcon, navigate]);

  return {
    onOpenFilters,
    onCloseFilters,
    routeChange,
    data: usersList,
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
    onAddUser,
    assignedUsersData,
    drawerState,
    onExpand,
    onRedirectToRole,
  };
};
